import { useState } from 'react';
import {
  File,
  FileText,
  FileCode,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  MoreVertical,
  Check,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { WorkspaceFile } from '../../types';
import { ContextMenu, MenuItem } from '../Common';
import clsx from 'clsx';

interface FileItemProps {
  file: WorkspaceFile;
  selected?: boolean;
  multiSelectMode?: boolean;
  onSelect?: () => void;
  onDoubleClick?: () => void;
  onContextMenu?: () => MenuItem[];
}

const getFileIcon = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();

  const iconMap: Record<string, typeof File> = {
    md: FileText,
    txt: FileText,
    json: FileCode,
    js: FileCode,
    ts: FileCode,
    jsx: FileCode,
    tsx: FileCode,
    py: FileCode,
    rb: FileCode,
    go: FileCode,
    rs: FileCode,
    java: FileCode,
    png: FileImage,
    jpg: FileImage,
    jpeg: FileImage,
    gif: FileImage,
    svg: FileImage,
    webp: FileImage,
    mp4: FileVideo,
    webm: FileVideo,
    mov: FileVideo,
    mp3: FileAudio,
    wav: FileAudio,
    ogg: FileAudio,
    zip: FileArchive,
    tar: FileArchive,
    gz: FileArchive,
    rar: FileArchive,
  };

  const IconComponent = iconMap[ext || ''] || File;
  return IconComponent;
};

const formatSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export function FileItem({
  file,
  selected,
  multiSelectMode,
  onSelect,
  onDoubleClick,
  onContextMenu,
}: FileItemProps) {
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const Icon = getFileIcon(file.name);
  const lastModified = file.lastModified
    ? formatDistanceToNow(new Date(file.lastModified), { addSuffix: true })
    : '--';

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onContextMenu) {
      setContextMenu({ x: e.clientX, y: e.clientY });
    }
  };

  return (
    <>
      <div
        className={clsx(
          'flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b',
          selected && 'bg-primary-50 hover:bg-primary-100'
        )}
        onClick={multiSelectMode ? onSelect : onDoubleClick}
        onDoubleClick={onDoubleClick}
        onContextMenu={handleContextMenu}
      >
        {multiSelectMode && (
          <div
            className={clsx(
              'w-5 h-5 border-2 rounded flex items-center justify-center',
              selected
                ? 'bg-primary-600 border-primary-600'
                : 'border-gray-300'
            )}
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.();
            }}
          >
            {selected && <Check className="w-3 h-3 text-white" />}
          </div>
        )}

        <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">
              {file.name}
            </span>
          </div>
          {file.lastCommit && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {file.lastCommit.message}
            </p>
          )}
        </div>

        <div className="text-sm text-gray-500 flex-shrink-0">
          {formatSize(file.size)}
        </div>

        <div className="text-sm text-gray-500 flex-shrink-0 w-28 text-right">
          {lastModified}
        </div>

        {onContextMenu && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleContextMenu(e);
            }}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        )}
      </div>

      {contextMenu && onContextMenu && (
        <ContextMenu
          items={onContextMenu()}
          position={contextMenu}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
}
